import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function PredictPromotionsPage() {
  const pageTitle = "Predict & Win - Promotions";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [promotionTitle, setPromotionTitle] = useState("");
  const [promotionDescription, setPromotionDescription] = useState("");
  const [promotionOpensAt, setPromotionOpensAt] = useState("");
  const [promotionClosesAt, setPromotionClosesAt] = useState("");
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const tableColumns = [
    { Header: "Promotion", accessor: "title", align: "left" },
    { Header: "Opens", accessor: "opens_at", align: "left" },
    { Header: "Closes", accessor: "closes_at", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Created", accessor: "created", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="button" fontWeight="medium">
              {row.title}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {row.description || "---"}
            </MDTypography>
          </MDBox>
        ),
        opens_at: row.opens_at || "---",
        closes_at: row.closes_at || "---",
        status: (
          <MDTypography
            variant="button"
            fontWeight="medium"
            color={row.status ? "success" : "text"}
          >
            {row.status ? "Active" : "Inactive"}
          </MDTypography>
        ),
        created: row.created || "---",
        actions: (
          <MDBox mt={1} variant="button" display="flex" gap={1}>
            <Tooltip title="Manage Promotion">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  navigate(`/tm/predict/promotions/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  settings
                </Icon>
              </MDButton>
            </Tooltip>
            <Tooltip title={row.status ? "Deactivate" : "Activate"}>
              <MDButton
                size="small"
                variant="outlined"
                color={row.status ? "warning" : "success"}
                iconOnly
                onClick={() => {
                  updatePromotionStatus(row);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  {row.status ? "pause" : "play_arrow"}
                </Icon>
              </MDButton>
            </Tooltip>
            <Tooltip title="Delete Promotion">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  setSelectedPromotion(row);
                  setOpenDeleteDialog(true);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      });
      return null;
    });
    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("/portal/tm/predict/promotions.json")
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRows(result.data.data.promotions || []);
            setPromotions(rows);
            setPageLoaded(true);
          } else {
            showMessage(result.data.data.message);
          }
        })
        .catch(() => {
          setShowLoading(false);
          showMessage(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
        });
    };
    fetchData();
  }, [refreshPage]);

  const resetPromotionForm = () => {
    setPromotionTitle("");
    setPromotionDescription("");
    setPromotionOpensAt("");
    setPromotionClosesAt("");
  };

  async function createPromotion() {
    if (!promotionTitle.trim()) {
      showMessage("Enter promotion title.");
      return;
    }
    if (!promotionOpensAt || !promotionClosesAt) {
      showMessage("Select the promotion opening and closing dates.");
      return;
    }
    if (new Date(promotionClosesAt) < new Date(promotionOpensAt)) {
      showMessage("The closing date cannot be earlier than the opening date.");
      return;
    }
    setOpenCreateDialog(false);
    setShowLoading(true);
    const payload = {
      title: promotionTitle.trim(),
      description: promotionDescription,
      opens_at: promotionOpensAt,
      closes_at: promotionClosesAt,
    };
    await axios
      .post("/portal/tm/predict/promotions/create.json", payload)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          resetPromotionForm();
          await statusDialog.show(
            result.data.data.message || "Promotion created successfully.",
            "success",
            "Success!"
          );
          setRefreshPage((value) => !value);
        } else {
          showMessage(result.data.data.message);
        }
      })
      .catch(() => {
        setShowLoading(false);
        showMessage(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
      });
  }

  async function updatePromotionStatus(row) {
    setShowLoading(true);
    await axios
      .get(`/portal/tm/predict/promotions/${row.reference}/status.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          showMessage(result.data.data.message, "success");
          setRefreshPage((value) => !value);
        } else {
          showMessage(result.data.data.message);
        }
      })
      .catch(() => {
        setShowLoading(false);
        showMessage(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
      });
  }

  async function deletePromotion() {
    if (!selectedPromotion) {
      return;
    }
    setOpenDeleteDialog(false);
    setShowLoading(true);
    await axios
      .get(`/portal/tm/predict/promotions/${selectedPromotion.reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          showMessage(result.data.data.message, "success");
          setSelectedPromotion(null);
          setRefreshPage((value) => !value);
        } else {
          showMessage(result.data.data.message);
        }
      })
      .catch(() => {
        setShowLoading(false);
        showMessage(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog
        open={openCreateDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => setOpenCreateDialog(false)}
      >
        <DialogTitle>
          <MDBox>Create Predict & Win Promotion</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={1}>
              <MDInput
                name="title"
                margin="dense"
                label="Promotion Title"
                type="text"
                fullWidth
                variant="outlined"
                value={promotionTitle}
                onChange={(e) => setPromotionTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="description"
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={promotionDescription}
                onChange={(e) => setPromotionDescription(e.target.value)}
              />
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="opens_at"
                margin="dense"
                label="Opens At"
                type="datetime-local"
                fullWidth
                variant="outlined"
                value={promotionOpensAt}
                onChange={(e) => setPromotionOpensAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="closes_at"
                margin="dense"
                label="Closes At"
                type="datetime-local"
                fullWidth
                variant="outlined"
                value={promotionClosesAt}
                onChange={(e) => setPromotionClosesAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateDialog(false);
              resetPromotionForm();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createPromotion();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        fullWidth
        maxWidth="xs"
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>
          <MDBox>Delete Promotion</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to delete <strong>{selectedPromotion?.title}</strong>?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDeleteDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>
          <MDButton
            color="error"
            variant="contained"
            onClick={() => {
              deletePromotion();
            }}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={1}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>emoji_events</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold" fontSize={16}>
                        Predict & Win Promotions
                      </MDTypography>
                      <MDTypography mt={1} variant="h6" fontWeight="light" fontSize={16}>
                        Create and manage prediction campaigns.
                        <br />
                        Configure questions, answers, prizes and participants.
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={3} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          resetPromotionForm();
                          setOpenCreateDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Create Promotion
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                          Promotions
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            resetPromotionForm();
                            setOpenCreateDialog(true);
                          }}
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox p={0}>
                    <DataTable
                      table={{ columns: tableColumns, rows: promotions }}
                      canSearch={false}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PredictPromotionsPage;
